import { tx as translateInterfaceText } from '@/utils/i18n';
import { LanguageSelector } from '@/components/layout/header/LanguageSelector';
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Mail } from "lucide-react";
import { z } from "zod";
import { authService } from "@/services/authService";
import { useCustomAuth } from "@/contexts/CustomAuthContext";
import { t } from "@/utils/i18n";
import { useLanguage } from "@/hooks/useLanguage";
import {
  AuthLayout,
  AuthCard,
  AuthCardHeader,
  AuthCardFooter,
  TextField,
  PasswordField,
  PrimaryButton,
  VisualPanel,
  ForgotPasswordModal,
} from "@/components/auth";

type LoginFormData = { email: string; password: string };

const Login = () => {
  useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const loginSchema = z.object({
    email: z.string().email(t('invalidEmailMsg')),
    password: z.string().min(1, t('requiredField')),
  });
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<LoginFormData>({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState<Partial<Record<keyof LoginFormData, string>>>({});
  const [rememberMe, setRememberMe] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const { login, isAuthenticated } = useCustomAuth();
  const redirectPath = location.state?.from?.pathname || "/dashboard";

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(redirectPath, { replace: true });
    }
  }, [isAuthenticated, navigate, redirectPath]);

  const handleChange = (field: keyof LoginFormData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    const result = loginSchema.safeParse(formData);

    if (!result.success) {
      const fieldErrors: Partial<Record<keyof LoginFormData, string>> = {};
      result.error.errors.forEach((error) => {
        const field = error.path[0] as keyof LoginFormData;
        fieldErrors[field] = error.message;
      });
      setErrors(fieldErrors);
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const user = {
        email: formData.email,
        senha: formData.password,
      };

      const res = await authService.login(user);

      if (res?.data?.success) {
        // Login successful
        login(
          res.data.user,
          res.data.token!,
          res.data.plano,
          rememberMe
        );

        toast.success(t('loginSuccess'));
        navigate(redirectPath, { replace: true });
      } else {
        toast.error(res?.data?.message || t('loginError'));
      }
    } catch (error: any) {
      console.error("Login error:", error);
      toast.error(error?.message || t('loginErrorGeneric'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout header={<div className="flex justify-end"><LanguageSelector compact /></div>} visualPanel={<VisualPanel />}>
      <AuthCard>
        {/* Logo above title - responsive sizing */}
        <img
          src="/assets/imgs/logo-azul-e-preta.png"
          alt={translateInterfaceText("HostVSL")}
          className="h-[34px] sm:h-[36px] lg:h-[42px] mb-[16px] lg:mx-auto"
        />

        <AuthCardHeader
          title={t('loginTitle')}
          subtitle={t('loginSubtitle')}
          className="mb-[20px] lg:text-center"
        />

        <form onSubmit={handleSubmit} className="w-full space-y-[12px] lg:max-w-[70%] lg:mx-auto">
          <TextField
            label={t('emailLabel')}
            type="email"
            placeholder={t('emailPlaceholder')}
            value={formData.email}
            onChange={handleChange("email")}
            error={errors.email}
            icon={<Mail className="h-4 w-4" />}
            autoComplete="email"
          />

          <PasswordField
            label={t('passwordLabel')}
            placeholder={t('passwordPlaceholder')}
            value={formData.password}
            onChange={handleChange("password")}
            error={errors.password}
            autoComplete="current-password"
          />

          <div className="flex items-center justify-between pt-[10px]">
            <label className="flex items-center gap-2.5 cursor-pointer select-none group">
              <div className="relative">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="peer sr-only"
                />
                <div
                  className="w-[18px] h-[18px] rounded-[5px] border transition-all duration-200 flex items-center justify-center peer-checked:bg-[#1F6BFF] peer-checked:border-[#1F6BFF] peer-focus-visible:ring-2 peer-focus-visible:ring-[#1F6BFF]/20 group-hover:border-[#C8D4E8]"
                  style={{ borderColor: rememberMe ? '#1F6BFF' : '#D8E2F2' }}
                >
                  {rememberMe && (
                    <svg width="10" height="8" viewBox="0 0 10 8" fill="none" className="text-white">
                      <path d="M1 4L3.5 6.5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </div>
              </div>
              <span
                className="text-[13px] leading-none"
                style={{ color: '#5B667A' }}
              >
                {t('stayConnected')}
              </span>
            </label>

            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              className="text-[13px] leading-none transition-all hover:underline underline-offset-2"
              style={{ color: '#1F6BFF' }}
            >
              {t('forgotPassword')}
            </button>
          </div>

          <div className="pt-[14px] space-y-3">
            <PrimaryButton type="submit" loading={isLoading} loadingText={t('enteringButton')}>
              {t('enterButton')}
            </PrimaryButton>
          </div>
        </form>

        <AuthCardFooter>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-0 text-center">
            <span style={{ color: '#5B667A' }}>{t('noAccount')}</span>
            <a
              href="/cadastro"
              className="inline-flex items-center gap-1 font-semibold transition-colors hover:underline group"
              style={{ color: '#1F6BFF' }}
            >
              <span className="sm:ml-1">{t('createAccountFree')}</span>
            </a>
          </div>
        </AuthCardFooter>
      </AuthCard>

      <ForgotPasswordModal
        open={showForgotPassword}
        onOpenChange={setShowForgotPassword}
      />
    </AuthLayout>
  );
};

export default Login;
